import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { RecommendationContext } from './recommendation-context';

/**
 * The Post Office-owned context every message instance event needs before it can be sent.
 */
const REQUIRED_MESSAGE_INSTANCE_EVENT_CONTEXT = [
	'messageInstanceId',
	'messageTemplateId',
	'placementId',
	'messageCreationType',
	'messageTriggerId',
] as const;

export type RequiredMessageInstanceEventContextField =
	(typeof REQUIRED_MESSAGE_INSTANCE_EVENT_CONTEXT)[number];

export type RequiredMessageInstanceEventContext = {
	messageInstanceId?: string;
	messageTemplateId?: string;
	placementId?: string;
	messageCreationType?: 'implicit' | 'explicit';
	messageTriggerId?: string;
};

/** The same context once every required field is known to be present. */
export type CompleteMessageInstanceEventContext = Required<RequiredMessageInstanceEventContext>;

/**
 * Returns the names of the required context fields that are absent or empty.
 */
export const getMissingMessageInstanceEventContext = (
	context: RequiredMessageInstanceEventContext,
): RequiredMessageInstanceEventContextField[] =>
	REQUIRED_MESSAGE_INSTANCE_EVENT_CONTEXT.filter((field) => !context[field]);

/**
 * Type-guard form of {@link getMissingMessageInstanceEventContext}.
 */
export const hasCompleteMessageInstanceEventContext = (
	context: RequiredMessageInstanceEventContext,
): context is CompleteMessageInstanceEventContext =>
	getMissingMessageInstanceEventContext(context).length === 0;

// https://switcheroo.atlassian.com/ui/gates/618e98de-bc3f-4433-a1f6-90c8ef68b0fb/key/post-office_enable_null-recommendation-ids
export const ENABLE_NULL_RECOMMENDATION_IDS = 'post-office_enable_null-recommendation-ids';

const toNullableId = (value?: string): string | null => (value ? value : null);

export const recommendationFromContext = (
	reco: RecommendationContext,
): {
	recommendationSessionId: string | null;
	recommendationEntityId: string | null;
	itemFeatureSetId: string | null;
} => {
	if (fg(ENABLE_NULL_RECOMMENDATION_IDS)) {
		return {
			recommendationSessionId: toNullableId(reco.sessionId),
			recommendationEntityId: toNullableId(reco.entityId),
			itemFeatureSetId: toNullableId(reco.itemFeatureSetId),
		};
	}

	return {
		recommendationSessionId: reco.sessionId ?? '',
		recommendationEntityId: reco.entityId ?? '',
		itemFeatureSetId: reco.itemFeatureSetId ?? '',
	};
};

export type ReportMissingContextArgs = {
	/** Which lifecycle event could not be constructed: `clicked`, `viewed`, `deliverySuccessful`, `dropped`. */
	messageEventType: string;
	missingFields: RequiredMessageInstanceEventContextField[];
	messageTemplateId?: string;
	placementId?: string;
	/** Present for `clicked` only, to identify which interaction was lost. */
	interactionIdentifier?: string;
	actionType?: string;
};
